import { Component, OnDestroy, OnInit } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import moment from 'moment';
import { combineLatest, iif, Observable, ReplaySubject } from 'rxjs';
import {
    filter,
    map,
    startWith,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as fromSettings from '../../../company-setting';
import { ScheduledDate } from '../../../lookups/store/schedule-date.model';
import * as fromScheduledDates from '../../../lookups/store/schedule-date.selectors';
import * as fromShared from '../../../shared';
import { ResponseStatus } from '../../../shared';
import { SelectItem } from '../../../shared/efaact-primeng.module';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import * as fromUser from '../../../user-info';
import { PayrollItem } from '../../item/store';
import * as payrollItemActions from '../../item/store/payroll-item.actions';
import * as fromPayrollItem from '../../item/store/payroll-item.selectors';
import * as fromStore from '../store';
import { PayrollCode } from '../store';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import * as fromDataServices from '../../../data-services';
@Component({
    selector: 'ef-third-party-payroll-container',
    templateUrl: './third-party-payroll-container.component.html',
    styleUrls: ['./third-party-payroll-container.component.scss'],
    standalone: false
})
export class ThirdPartyPayrollContainerComponent implements OnInit, OnDestroy {
    process$: Observable<fromShared.Process>;
    userInfo$: Observable<fromUser.IUserInfo>;
    payrollCodes$: Observable<fromStore.PayrollCode[]>;
    payrollCodesSelectItems$: Observable<SelectItem[]>;
    payrollCodesColumnHeaderSelectItems$: Observable<SelectItem[]>;
    payrollCodesDescSelectItems$: Observable<SelectItem[]>;
    payrollItems$: Observable<PayrollItem[]>;
    payrollItemsSelectItems$: Observable<SelectItem[]>;
    thirdPartyPayroll$: Observable<fromStore.ThirdPartyPayroll[]>;
    externalIntegration$: Observable<boolean>;
    reimburseViaPayroll$: Observable<boolean>;
    fileFormats$: Observable<any[]>;
    availableColumns$: Observable<any[]>;
    payrollExportFormat$: Observable<string>;
    SavePayrollItemSubject = new ReplaySubject<PayrollItem[]>(1);
    payPeriodDatesSelectItems$: Observable<SelectItem[]>;
    payPeriodDates$: Observable<ScheduledDate[]>;
    qboOnline$: Observable<boolean>;
    expenseReimbPaycodeSetting$: Observable<string>;
    expenseReimbPaycodeOptions$: Observable<SelectItem[]>;
    reimbursementPaycodes$: Observable<fromStore.ReimbursementPaycode[]>;
    selectedExpenseReimbPaycode: string | null = null;
    reimburseViaPayrollChecked = false;
    showReimburseWarning = false;
    showReimbPaycodeDialog = false;
    dialogExpenseReimbPaycode: string | null = null;
    private payCodeAddTemplate: fromStore.PayrollCode = {
        id: 'add',
        Paycodeid: -1,
        Paycode: '',
        Paycodedesc: '',
        Colheader: '',
        Status: {
            Message: 'Adding',
            StatusCode: ResponseStatus.None
        },
        IsLWOP: false,
        IsPremium: false
    };
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    PayrollExportFormatChangedSubject = new ReplaySubject<string>(1);
    ExportSubject = new ReplaySubject<{
        scheduledDateID: string;
        codeAsRow: boolean;
    }>(1);
    active = true;
    constructor(
        private store: Store<fromStore.State>,
        private log: LoggingService
    ) {
        this.setupObservables();
        this.setupSubscriptions();
    }

    ngOnInit(): void {
        // this.store.dispatch(fromStore.getThirdPartyPayrolls());
        this.store.dispatch(fromStore.getPayrollCodes());
        this.store.dispatch(fromStore.getFileFormat());
        this.store.dispatch(payrollItemActions.getPayrollItems());
        // for govconpay since they are currently the only external api integration this is true or false
        // will probably need to determine who the external integrator is and based on that determine if the
        // export format is fixed or not.  RN govconpay is fixed format.
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'ExternalEmployeeSource'
            })
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'PayrollExportFormat'
            })
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: fromSettings.CompanySettingEnum.ReimburseViaPayroll
            })
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType:
                    fromSettings.CompanySettingEnum.ExpenseReimbursementPaycode
            })
        );
        this.store.dispatch(fromStore.getReimbursementPaycodes());
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    setupObservables() {
        this.process$ = this.store.pipe(
            select(fromShared.selectAll),
            map(x => x[0])
        );
        this.userInfo$ = this.store.pipe(
            select(fromUser.getUserInfo)
            // map(x => x.PrevPayPeriodEnd)
        );
        this.externalIntegration$ = this.store.pipe(
            select(
                fromSettings.getSettingBooleanValue('ExternalEmployeeSource')
            )
        );
        this.reimburseViaPayroll$ = this.store.pipe(
            select(fromSettings.isReimburseViaPayrollEnabled)
        );

        this.payPeriodDates$ = this.store.pipe(
            select(
                fromScheduledDates.selectScheduledDateType({
                    ScheduledDateType: 'PPE'
                })
            )
        );
        this.payPeriodDatesSelectItems$ = this.payPeriodDates$.pipe(
            map(dates =>
                dates.map(d => ({
                    label: `${moment(d.ScheduledStartDate).format(
                        'MM/DD/YY'
                    )} - ${moment(d.ScheduledEndDate).format('MM/DD/YY')} ${
                        d.ScheduledDatePurpose
                    }`,
                    value: d.ScheduledDateID
                }))
            )
        );
        this.payrollCodes$ = this.store.pipe(
            select(fromStore.selectPayrollCodes),
            map(x => clone(x) as fromStore.PayrollCode[]),
            withLatestFrom(this.externalIntegration$),
            map(([paycodes, gcp]) =>
                gcp ? paycodes : [{ ...this.payCodeAddTemplate }, ...paycodes]
            )
        );
        this.reimbursementPaycodes$ = this.store.pipe(
            select(fromStore.selectReimbursementPaycodes)
        );
        this.expenseReimbPaycodeOptions$ = this.reimbursementPaycodes$.pipe(
            tap(codes =>
                this.log.verbose({
                    type: 'ThirdPartyPayrollContainerComponent:expenseReimbPaycodeOptions',
                    codes
                })
            ),
            map(codes =>
                codes.map(x => ({
                    label: `${x.Description ?? x.Title ?? x.PayCode} (${x.PayCode})`,
                    value: x.PayCode
                }))
            ),
            tap(options =>
                this.log.verbose({
                    type: 'ThirdPartyPayrollContainerComponent:expenseReimbPaycodeOptions',
                    options
                })
            )
        );
        this.payrollCodesSelectItems$ = this.payrollCodes$.pipe(
            map(c => c.filter(x => x.Paycodeid >= 0)),
            map(c =>
                c.map(x => ({
                    label: `${x.Paycode}${
                        x.Paycodedesc ? `- ${x.Paycodedesc}` : ''
                    }`,
                    value: x.Paycode
                }))
            )
        );
        this.payrollCodesColumnHeaderSelectItems$ = this.payrollCodes$.pipe(
            map(c => c.filter(x => x.Paycodeid >= 0)),
            map(c => fromShared.uniqBy(c, x => x.Colheader) as PayrollCode[]),
            map(c =>
                c.map(x => ({
                    label: x.Colheader,
                    value: x.Colheader
                }))
            )
        );
        this.payrollCodesDescSelectItems$ = this.payrollCodes$.pipe(
            map(c => c.filter(x => x.Paycodeid >= 0 && x.Paycodedesc != null)),
            map(c =>
                c.map(x => ({
                    label: x.Paycodedesc,
                    value: x.Paycodedesc
                }))
            )
        );
        this.payrollItems$ = this.store.pipe(
            select(fromPayrollItem.selectAll),
            map(x => clone(x) as PayrollItem[])
        );
        this.payrollItemsSelectItems$ = this.store.pipe(
            select(fromPayrollItem.selectActivePayrollItems),
            map(p =>
                p.map(x => ({
                    label: x.PayrollItem,
                    value: x.PIListID
                }))
            )
        );

        let seq = 0;
        this.thirdPartyPayroll$ = this.store.pipe(
            select(fromStore.selectAll),
            map(x => clone(x) as fromStore.ThirdPartyPayroll[])
        );
        this.fileFormats$ = this.store.pipe(
            select(fromStore.selectFileFormats),
            map(x => clone(x) as fromStore.FileFormatModel[]),
            tap(x => (seq = x.reduce((a, c) => Math.max(a, c.Sequencenbr), 0))),
            map(x =>
                x.map(y => {
                    if (y.Sequencenbr) {
                        seq = y.Sequencenbr + 1;
                    }
                    return { ...y, sequence: y.Sequencenbr || seq };
                })
            ),
            map(x => x.sort((a, b) => a.Sequencenbr - b.Sequencenbr))
        );
        this.payrollExportFormat$ = this.store.pipe(
            select(fromSettings.selectEntities),
            map(
                (x: Dictionary<fromSettings.CompanySetting>) =>
                    x['PayrollExportFormat'] &&
                    x['PayrollExportFormat'].SettingValue
            )
        );
        this.expenseReimbPaycodeSetting$ = this.store.pipe(
            select(fromSettings.selectEntities),
            map(
                (x: Dictionary<fromSettings.CompanySetting>) =>
                    x['ExpenseReimbursementPaycode'] &&
                    (x['ExpenseReimbursementPaycode']
                        .SettingValue as string)
            )
        );
        this.qboOnline$ = this.store.pipe(select(fromSettings.isQBOEnabled));
    }
    setupSubscriptions() {
        this.log.verbose({
            type: 'ThirdPartyPayrollContainerComponent:setupSubscriptions'
        });
        this.reimburseViaPayroll$
            .pipe(takeWhile(() => this.active))
            .subscribe(enabled => {
                this.log.verbose({
                    type: 'ThirdPartyPayrollContainerComponent:reimburseViaPayroll$',
                    enabled
                });
                this.reimburseViaPayrollChecked = enabled;
                this.log.verbose({
                    type: 'ThirdPartyPayrollContainerComponent:reimburseViaPayroll$:state',
                    reimburseViaPayrollChecked: this.reimburseViaPayrollChecked,
                    showReimburseWarning: this.showReimburseWarning
                });
            });
        combineLatest([
            this.reimbursementPaycodes$,
            this.expenseReimbPaycodeSetting$
        ])
            .pipe(
                takeWhile(() => this.active),
                tap(([codes, current]) => this.log.verbose({
                    type: 'ThirdPartyPayrollContainerComponent:getReimbursementPaycodes',
                    codes,
                    current
                }))
            )
            .subscribe(([codes, current]) => {
                if (!current) {
                    this.selectedExpenseReimbPaycode = null;
                    return;
                }
                const match = codes.some(
                    c => (c.PayCode || '').trim() === current.trim()
                );
                this.selectedExpenseReimbPaycode = match ? current : null;
            });
        this.ShowErrorSubject.pipe(
            withLatestFrom(this.process$.pipe(startWith(null))),
            takeWhile(() => this.active)
        ).subscribe(([error, process]) => {
            this.log.verbose({
                type: 'ThirdPartyPayrollContainerComponent:onShowError$',
                process,
                error
            });
            this.store.dispatch(
                new fromShared.Errors.SetShowErrors({
                    open: error.open,
                    MsgEntityID: error.MsgEntityID,
                    MsgProcessID: (process && process.ProcessID) ?? undefined
                })
            );
        });

        combineLatest([
            this.payrollExportFormat$,
            this.PayrollExportFormatChangedSubject
        ])
            .pipe(
                takeWhile(() => this.active),
                filter(
                    ([prev, next]) =>
                        prev !== next && next !== null && next !== undefined
                )
            )
            .subscribe(([prev, next]) => {
                this.log.verbose({
                    type: 'ThirdPartyPayrollContainerComponent:PayrollExportFormatChangedSubject',
                    prev,
                    next
                });
                this.store.dispatch(
                    fromSettings.putCompanySettings({
                        settings: [
                            {
                                SettingType: 'PayrollExportFormat',
                                SettingValue: next
                            }
                        ]
                    })
                );
            });
        this.SavePayrollItemSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.payrollItems$)
        ).subscribe(([items, payrolls]) => {
            this.log.verbose({
                type: 'ThirdPartyPayrollContainerComponent:SavePayrollItemSubject',
                items,
                payrolls
            });
            this.store.dispatch(
                fromStore.putPayrollItems({
                    items: items.map(i => {
                        const p = payrolls.find(x => x.PIListID === i.PIListID);
                        return {
                            ...p,
                            ExportCode: i.ExportCode
                        };
                    })
                })
            );
        });

        this.ExportSubject.pipe(
            withLatestFrom(this.payPeriodDates$),
            takeWhile(() => this.active)
        ).subscribe(([e, payDates]) => {
            const pd = payDates.find(
                x => x.ScheduledDateID === e.scheduledDateID
            );
            if (pd) {
                this.store.dispatch(
                    fromStore.getPayrollExport({
                        codeAsRow: e.codeAsRow,
                        startDate: pd.ScheduledStartDate,
                        endDate: pd.ScheduledEndDate
                    })
                );
            }
        });
    }
    onShowError($event) {
        this.log.verbose({
            type: 'ThirdPartyPayrollContainerComponent:onShowError',
            $event
        });
        this.ShowErrorSubject.next($event);
    }
    onSaveFileFormat($event) {
        this.log.verbose({
            type: 'ThirdPartyPayrollContainerComponent:onSaveFileFormat',
            $event
        });
        this.store.dispatch(fromStore.putFileFormat({ fileFormats: $event }));
    }
    onSavePayrollItem($event: PayrollItem[]) {
        this.log.verbose({
            type: 'ThirdPartyPayrollContainerComponent:onSavePayrollItem',
            $event
        });
        this.SavePayrollItemSubject.next($event);
    }
    onSavePayrollCode($event: PayrollCode[]) {
        this.log.verbose({
            type: 'ThirdPartyPayrollContainerComponent:onSavePayrollCode',
            $event
        });
        this.store.dispatch(
            fromStore.putPayrollCodes({ payrollCodes: $event })
        );
    }
    onDeletePayrollCode($event: PayrollCode) {
        this.log.verbose({
            type: 'ThirdPartyPayrollContainerComponent:onDeletePayrollCode',
            $event
        });
        this.store.dispatch(
            fromStore.confirmRemovePayrollCode({ payrollCode: $event })
        );
    }
    onExport($event) {
        this.log.verbose({
            type: 'ThirdPartyPayrollContainerComponent:onExport',
            $event
        });
        this.ExportSubject.next($event);
    }
    onPayrollExportFormatChanged($event) {
        this.PayrollExportFormatChangedSubject.next($event);
        this.store.dispatch(fromStore.clearThirdPartyPayrolls());
    }
    onExpenseReimbPaycodeChanged($event: string | null) {
        this.store.dispatch(
            fromSettings.putCompanySettings({
                settings: [
                    {
                        SettingType: 'ExpenseReimbursementPaycode',
                        SettingValue: $event
                    }
                ]
            })
        );
    }
    onReimburseViaPayrollChanged(checked: boolean) {
        this.log.verbose({
            type: 'ThirdPartyPayrollContainerComponent:onReimburseViaPayrollChanged',
            checked,
            reimburseViaPayrollChecked_before: this.reimburseViaPayrollChecked,
            showReimburseWarning_before: this.showReimburseWarning
        });
        this.reimburseViaPayrollChecked = checked;
        this.store.dispatch(
            fromSettings.putCompanySettings({
                settings: [
                    {
                        SettingType: 'reimburseviapayroll',
                        SettingValue: checked ? '1' : '0'
                    }
                ]
            })
        );
        if (checked) {
            this.log.verbose({
                type: 'ThirdPartyPayrollContainerComponent:onReimburseViaPayrollChanged:checked-true'
            });
            this.dialogExpenseReimbPaycode =
                this.selectedExpenseReimbPaycode || null;
            this.showReimbPaycodeDialog = true;
            this.showReimburseWarning = false;
        } else {
            this.log.verbose({
                type: 'ThirdPartyPayrollContainerComponent:onReimburseViaPayrollChanged:checked-false'
            });
            this.showReimbPaycodeDialog = false;
            this.selectedExpenseReimbPaycode = null;
            // This handler is only called on user interaction; when user unchecks, show the warning.
            this.showReimburseWarning = true;
        }
        this.log.verbose({
            type: 'ThirdPartyPayrollContainerComponent:onReimburseViaPayrollChanged:after',
            reimburseViaPayrollChecked: this.reimburseViaPayrollChecked,
            showReimburseWarning: this.showReimburseWarning
        });
    }
    onReimbPaycodeDialogOk() {
        if (!this.dialogExpenseReimbPaycode) {
            return;
        }
        this.selectedExpenseReimbPaycode = this.dialogExpenseReimbPaycode;
        this.showReimbPaycodeDialog = false;
        this.onExpenseReimbPaycodeChanged(this.selectedExpenseReimbPaycode);
    }
}
